import PropTypes from "prop-types";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Typography,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";
import _ from "lodash";
import MultiCheckboxes from "./MultiCheckboxes";
import ScheduleRuleField from "./ScheduleRuleField";
import { FilledSelect } from "../../components/FormElements";
import { capitalize } from "../../util/helpers";
import { withSnackbar } from "../../HOC/withSnackbar";
import {
  TWENTY_FOUR_HOURS_IN_MINUTES,
  GEOFENCE_SCHEDULE_TYPES,
  MONTHS_OF_YEAR,
  DAYS_OF_WEEK,
} from "../../constants";
import formToSchedule from "../../util/parsers/formToSchedule";
import convertScheduleTz from "../../util/parsers/convertScheduleTz";
import compactRules from "../../util/lists/compactRules";

import { FleetContext, GeofencesContext } from "../../context";
import { zoneFlagsFromLayerType } from "../../services/geofences.service";

const useStyles = makeStyles((theme) => ({
  tooltip: {
    paddingLeft: theme.spacing(1),
    height: theme.spacing(2),
  },
  leftHandField: {
    paddingRight: theme.spacing(2),
  },
  repetitionTitles: {
    padding: theme.spacing(2, 0),
    fontWeight: 300,
  },
  collapseSection: {
    paddingTop: theme.spacing(1),
  },

  tooltipTitleWrapper: {
    padding: theme.spacing(2, 0),
  },
}));

const CreateScheduleRules = ({ setModalOpen, setSnackbarMessage }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles(theme);
  const currentMonth = moment().month();
  const { layers, selectedZone, createAPIScheduledGeofence } = useContext(GeofencesContext);
  const { activeFleet } = useContext(FleetContext);

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [moreThan24Hrs, setMoreThan24Hrs] = useState(false);
  const [disableDropdown, setDisableDropdown] = useState(true);
  const [timeDiffError, setTimeDiffError] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [form, setForm] = useState({
    scheduleName: "",
    startDate: "",
    startTime: "",
    endTime: "",
    endDate: "",
    scheduleType: GEOFENCE_SCHEDULE_TYPES.doesNotRepeat,
    selectedMonthDropdown: MONTHS_OF_YEAR[currentMonth],
    datesOfMonth: "",
    selectedMonths: [],
    selectedWeekDays: [],
  });

  useEffect(() => {
    const start = moment(`${form.startDate} ${form.startTime}`, "YYYY-MM-DDT HH:mm");
    const end = moment(`${form.endDate} ${form.endTime}`, "YYYY-MM-DDT HH:mm");
    const diffInMinutes = end.diff(start, "minutes");
    const greaterThan24hrs = diffInMinutes > TWENTY_FOUR_HOURS_IN_MINUTES;

    if (
      greaterThan24hrs &&
      (form.scheduleType === GEOFENCE_SCHEDULE_TYPES.datesOfMonth ||
        form.scheduleType === GEOFENCE_SCHEDULE_TYPES.dayOfWeek)
    ) {
      setForm({ ...form, scheduleType: GEOFENCE_SCHEDULE_TYPES.doesNotRepeat });
    }
    setMoreThan24Hrs(greaterThan24hrs);
  }, [form]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setModalOpen(false);
    setOpen(false);
  };

  useEffect(() => {
    const startTime = form.startTime;
    const endTime = form.endTime;
    const startDate = form.startDate;
    const endDate = form.endDate;

    const parsedStartTime = parseFloat(startTime.replace(":", "."));
    const parsedEndTime = parseFloat(endTime.replace(":", "."));

    if (_.isEqual(startDate, endDate) && parsedEndTime <= parsedStartTime) {
      setErrorText(`${t("Fleet.Geofences.ScheduleRules.TimeDifferenceError")}`);
      setTimeDiffError(true);
    } else {
      setTimeDiffError(false);
      setErrorText(null);
    }
  }, [form.endDate, form.endTime, form.startDate, form.startTime]);

  const handleSubmit = async () => {
    setLoading(true);
    const layer = layers[selectedZone.parent];
    const zone = layer.zones[selectedZone.key];

    const localTimeRules = formToSchedule(form);
    const utcTimeRules = convertScheduleTz(localTimeRules, activeFleet.timezone, "UTC");
    const compactedRules = compactRules(utcTimeRules);

    // zoneFlags contains zone/layer related attributes. ie: isWarehouse, noParking, etc..
    const zoneFlags = zoneFlagsFromLayerType(layer.type);

    const postData = {
      marketUrl: activeFleet.market,
      fleetUrl: activeFleet.url,
      name: activeFleet.name,
      zoneId: zone.id,
      active: zone.active,
      h3Indexes: Object.keys(zone.hexes),
      polygon: zone.polygon,
      scheduleName: compactedRules.scheduleName,
      rules: compactedRules.rules,
      incentive: layer.incentive,
      speedLimit: layer.speed,
      ...zoneFlags,
    };

    try {
      await createAPIScheduledGeofence(postData);
      setModalOpen(false);
      handleClose();
    } catch (err) {
      console.error(err);
      if (err.response) {
        const { data, status, statusText } = err.response;
        const message = `Error: ${status} ${statusText}. Detail: ${JSON.stringify(data)}`;
        setSnackbarMessage(message);
      } else {
        setSnackbarMessage(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (item, selectionType, checked) => {
    let selected = [];
    if (checked) {
      selected = [...form[selectionType], item];
    } else {
      selected = form[selectionType].filter((selectedItem) => selectedItem !== item);
    }
    setForm({
      ...form,
      [selectionType]: selected,
    });
  };

  const handleInput = (field, e) => {
    let inputData = {
      [field]: e.target.value,
    };

    if (field === "startDate") {
      inputData = {
        ...inputData,
        ...getAutoSelections(e.target.value),
      };
    }
    setForm({ ...form, ...inputData });
  };

  useEffect(() => {
    const timeFieldsFilled = form.startDate && form.endDate && form.startTime && form.endTime;
    setDisableDropdown(!timeFieldsFilled);
  }, [form.startDate, form.endDate, form.startTime, form.endTime]);

  const getAutoSelections = (startDate) => {
    const dateEntered = moment(startDate);
    const weekday = dateEntered.weekday();
    const month = dateEntered.month();
    const autoSelectedWeekDay = DAYS_OF_WEEK[weekday];
    const autoSelectedMonth = MONTHS_OF_YEAR[month];

    return {
      selectedWeekDays: _.uniq([...form.selectedWeekDays, autoSelectedWeekDay]),
      selectedMonths: _.uniq([...form.selectedMonths, autoSelectedMonth]),
      selectedMonthDropdown: autoSelectedMonth,
    };
  };

  return (
    <Dialog open={open} onClose={() => handleClose()} maxWidth="md" fullWidth>
      <DialogTitle>{t("Fleet.Geofences.ScheduleRules.CreateScheduleRules")}</DialogTitle>
      <DialogContent>
        <Grid container justify="space-between">
          <Grid item xs={12} md={12}>
            <ScheduleRuleField
              form={form}
              field="scheduleName"
              callback={(e) => handleInput("scheduleName", e)}
            />
          </Grid>
          <Typography variant="h6" className={classes.repetitionTitles}>
            {t("Fleet.Geofences.ScheduleRules.EnterFirstTimeBlock")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <ScheduleRuleField
                form={form}
                field="startDate"
                showTz
                callback={(e) => handleInput("startDate", e)}
                otherProps={{ type: "date" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ScheduleRuleField
                form={form}
                field="startTime"
                showTz
                callback={(e) => handleInput("startTime", e)}
                otherProps={{ type: "time" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ScheduleRuleField
                form={form}
                field="endTime"
                showTz
                callback={(e) => handleInput("endTime", e)}
                otherProps={{
                  type: "time",
                  error: timeDiffError === true,
                  helperText: errorText,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ScheduleRuleField
                form={form}
                field="endDate"
                showTz
                callback={(e) => handleInput("endDate", e)}
                otherProps={{ type: "date", inputProps: { min: form.startDate } }}
              />
            </Grid>
          </Grid>
          <Grid item className={classes.leftHandField} xs={12} md={3}>
            <FilledSelect
              value={form.scheduleType}
              label={t("Fleet.Geofences.ScheduleRules.ScheduleType")}
              onChange={(e) => handleInput("scheduleType", e)}
              FormControlProps={{ fullWidth: true, margin: "normal" }}
              SelectProps={{ disabled: disableDropdown }}
            >
              {Object.entries(GEOFENCE_SCHEDULE_TYPES).map(([key, scheduleType]) => (
                <MenuItem
                  value={scheduleType}
                  key={scheduleType}
                  disabled={
                    moreThan24Hrs &&
                    (scheduleType === GEOFENCE_SCHEDULE_TYPES.datesOfMonth ||
                      scheduleType === GEOFENCE_SCHEDULE_TYPES.dayOfWeek)
                  }
                >
                  {t(`Fleet.Geofences.ScheduleTypes.${capitalize(key)}`)}
                </MenuItem>
              ))}
            </FilledSelect>
          </Grid>
        </Grid>
        {/* Dates Of Month Repetition Section */}
        <Collapse
          in={form.scheduleType === GEOFENCE_SCHEDULE_TYPES.datesOfMonth}
          className={classes.collapseSection}
          timeout={1000}
        >
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container className={classes.tooltipTitleWrapper}>
            <Typography variant="h6" className={classes.repetitionTitles}>
              {t("Fleet.Geofences.ScheduleRules.EnterEachStartDate")}
              <Tooltip
                className={classes.tooltip}
                placement="right"
                title={t("Fleet.Geofences.ScheduleRules.DatesOfMonthTooltip")}
              >
                <InfoIcon />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FilledSelect
                value={form.selectedMonthDropdown}
                label="Month"
                onChange={(e) => handleInput("selectedMonthDropdown", e)}
                FormControlProps={{ fullWidth: true, margin: "normal" }}
              >
                {MONTHS_OF_YEAR.map((month) => (
                  <MenuItem value={month} key={month}>
                    {month}
                  </MenuItem>
                ))}
              </FilledSelect>
            </Grid>
            <Grid item xs={12} md={6}>
              <ScheduleRuleField
                form={form}
                field="datesOfMonth"
                callback={(e) => handleInput("datesOfMonth", e)}
                otherProps={{ placeholder: "1, 3-7, 10" }}
              />
            </Grid>
          </Grid>
        </Collapse>
        {/* Day Of Week Repetition Options */}
        <Collapse
          in={form.scheduleType === GEOFENCE_SCHEDULE_TYPES.dayOfWeek}
          className={classes.collapseSection}
          timeout={1000}
        >
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container className={classes.tooltipTitleWrapper}>
            <Typography variant="h6" className={classes.repetitionTitles}>
              {t("Fleet.Geofences.ScheduleRules.SelectEachStartDay")}
              <Tooltip
                className={classes.tooltip}
                placement="right"
                title={t("Fleet.Geofences.ScheduleRules.DayOfWeekToolTip")}
              >
                <InfoIcon />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid container>
            <MultiCheckboxes
              form={form}
              collection={DAYS_OF_WEEK}
              selectionType="selectedWeekDays"
              callback={handleCheckboxChange}
            />
          </Grid>
          <Typography variant="h6" className={classes.repetitionTitles}>
            {t("Fleet.Geofences.ScheduleRules.RepeatMonth")}
          </Typography>
          <Grid container>
            <MultiCheckboxes
              form={form}
              collection={MONTHS_OF_YEAR}
              selectionType="selectedMonths"
              callback={handleCheckboxChange}
            />
          </Grid>
        </Collapse>
        {/* Weekly Repetition Options */}
        <Collapse
          in={form.scheduleType === GEOFENCE_SCHEDULE_TYPES.weekly}
          className={classes.collapseSection}
          style={{ paddingBottom: theme.spacing(3) }}
          timeout={1000}
        >
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container>
            <Typography variant="h6" className={classes.repetitionTitles}>
              {t("Fleet.Geofences.ScheduleRules.RepeatMonth")}
              <Tooltip
                className={classes.tooltip}
                placement="right"
                title={t("Fleet.Geofences.ScheduleRules.RepeatMonthToolTip")}
              >
                <InfoIcon />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid container>
            <MultiCheckboxes
              form={form}
              collection={MONTHS_OF_YEAR}
              selectionType="selectedMonths"
              callback={handleCheckboxChange}
            />
          </Grid>
        </Collapse>
        {loading && <LinearProgress color="secondary" />}
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("Fleet.Geofences.ScheduleRules.Cancel")}</Button>
        <Button
          disabled={
            !form.scheduleName ||
            !form.startDate ||
            !form.endDate ||
            !form.startTime ||
            !form.endTime ||
            timeDiffError === true
          }
          onClick={handleSubmit}
        >
          {t("Fleet.Geofences.ScheduleRules.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withSnackbar(CreateScheduleRules);

CreateScheduleRules.propTypes = {
  setModalOpen: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
};
